import type { TypedDataSigner } from '@ethersproject/abstract-signer'
import { Signer } from 'ethers'
import React, { CSSProperties, FC, useEffect, useState } from 'react'
import styles from '~/modules/Cards/Infrastructure/Components/MintableCardCard.module.scss'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'
import { useAddress, useChainId, useSetIsWalletModalOpen, useSigner } from '@thirdweb-dev/react'
import requestMint from '~/modules/Cards/RequestMint/Application/RequestMint'
import { ComponentCardDto } from '~/modules/Products/Infrastructure/Dtos/ComponentCardDto'

interface Props {
  cardDto: ComponentCardDto
}

export const MintableCardCard: FC<Props> = ({ cardDto }) => {
  const { t } = useTranslation('cards')
  const setIsWalletModalOpen = useSetIsWalletModalOpen()
  const address = useAddress()
  const signer = useSigner()
  const chainId = useChainId()
  const connected = address !== undefined && signer !== undefined && chainId !== undefined
  const [mintStatus, setMintStatus] = useState(cardDto.mintStatus)
  const [canMint, setCanMint] = useState(false)

  useEffect(() => {
    setCanMint(mintStatus === 'pending' && connected)
  }, [mintStatus, connected])

  const mint = async () => {
    if (!connected || !cardDto.id) {
      return
    }

    setMintStatus('minting')
    try {
      await requestMint(
        signer as Signer & TypedDataSigner,
        chainId,
        cardDto.id,
        address
      )
      setMintStatus('minted')
    } catch (exception: unknown) {
      setMintStatus('pending')
      console.log(exception)
    }
  }

  let connectOrMintButton

  if (connected) {
    if (mintStatus === 'minted') {
      connectOrMintButton = (
        <button
          className={ `
            ${styles.mintableCardCard__mintButton}
            ${styles.mintableCardCard__mintButton_disabled}
          ` }
          disabled
        >
          { t('mint_card_button_done') }
        </button>
      )
    } else {
      connectOrMintButton = (
        <button
          className={ `
            ${styles.mintableCardCard__mintButton}
            ${canMint ? '' : styles.mintableCardCard__mintButton_disabled}
          ` }
          onClick={ mint }
          disabled={ !canMint }
        >
          { t('mint_card_button') }
        </button>
      )
    }
  } else {
    connectOrMintButton = (
      <button
        className={ styles.mintableTrophyCard__mintButton }
        onClick={ () => setIsWalletModalOpen(true) }
      >
        { t('mint_card_connect_wallet_button') }
      </button>
    )
  }

  return (
    <div
      className={ styles.mintableCardCard__container }
    >
      <Link
        href={ `/cards/${cardDto.id}` }
        className={ styles.mintableCardCard__link }
      >
        { t('show_info_button') }
      </Link>

      <div
        className={ styles.mintableCardCard__imageWrapper }>
        <img
          className={ styles.mintableCardCard__media }
          src={ cardDto.imageUrl }
          alt={ cardDto.description }
          style={ {
            '--bg-color': cardDto.organizer.color,
          } as CSSProperties }
        />
      </div>

      <div className={ styles.mintableCardCard__data }>
        <div className={ styles.mintableCardCard__organizer }>
          { cardDto.content.header }
        </div>
        <div className={ styles.mintableCardCard__fullName }>
          { cardDto.content.subtitle }
        </div>
        <div className={ styles.mintableCardCard__extra }>
          { cardDto.content.title }
        </div>
      </div>

      { connectOrMintButton }
    </div>
  )
}
